import { ActivityPurchaseModel } from 'api-hooks/order/activity';
import { CruisePurchaseModel } from 'api-hooks/order/cruise';
import { HotelPurchaseModel } from 'api-hooks/order/hotel';
import { FlightPurchaseModel } from 'api-hooks/order/ticket-order';
import { DocumentPurchaseModel } from 'api-hooks/order/travel-document';
import { ProductPurchaseModel } from 'api-hooks/product/order';
import { distinct } from 'common/utils/iterable';
import { capitalize, generateId } from 'common/utils/string';
import { generateUniqueAirlineItem } from 'legacy-modules/order/ticket-order/hooks/use-get-default-values';
import { guestNameTransformer } from 'legacy-modules/sales/ticket-sale/hooks/use-get-default-values';

export default function defaultValuesPurchase(
  data:
    | FlightPurchaseModel
    | HotelPurchaseModel
    | CruisePurchaseModel
    | DocumentPurchaseModel
    | ActivityPurchaseModel
    | ProductPurchaseModel,
  transactionType?: string,
) {
  const defaultValues: any = {
    transactionType,
    transactionAt: data.transactionAt,
    type: capitalize(data.type),
    number: data.number,
    agent: data.agent.name,
    paymentChannel: data.paymentChannel?.name ?? '',
    status: data.status,
    waitingId: data.waitingId,
    tourId: data.tour?.id,
    tourLabel: data.tour?.code,
    invoiceNumber: data.invoiceNumber,
    invoiceRefId: data.invoiceRefId,
  };

  if (transactionType) {
    if (transactionType === 'activity') {
      const activityPurchase = data as ActivityPurchaseModel;

      return {
        ...defaultValues,
        activityPurchases: (activityPurchase.activityPurchases || []).map(
          (item) => {
            const activityCustomIds: string[] = item.isGroupedPrice
              ? item.activityPurchaseGuests[0].activityPurchaseGuestDetails.map(
                  () => generateId(),
                )
              : [];
            return {
              id: item.id,
              isGroupedPrice: item.isGroupedPrice,
              activityPurchaseGuests: (item.activityPurchaseGuests || []).map(
                (guest) => ({
                  id: guest.id,
                  guestId: guest.guest.id,
                  guestName: `${capitalize(guest.guest.title)} ${
                    guest.guest.name
                  }`,
                  activityPurchaseGuestDetails: (
                    guest.activityPurchaseGuestDetails || []
                  ).map((asgd, idx) => ({
                    id: asgd.id,
                    customId: activityCustomIds[idx],
                    price: parseFloat(asgd.price.toString()),
                    description: asgd.description,
                    activityId: asgd.activity.id,
                    date: asgd.date,
                    activityName: asgd.activity.name,
                    type: asgd.activity.type,
                  })),
                }),
              ),
              activityPurchaseAddOns: (item.activityPurchaseAddOns || []).map(
                (addons) => ({
                  id: addons.id,
                  guestId: addons.guest.id,
                  guestName: `${capitalize(addons.guest.title)} ${
                    addons.guest.name
                  }`,
                  price: addons.price,
                  description: addons.description,
                }),
              ),
            };
          },
        ),
      };
    }
    if (transactionType === 'cruise') {
      const cruisePurchase = data as CruisePurchaseModel;
      return {
        ...defaultValues,
        cruisePurchases: (cruisePurchase.cruisePurchases || []).map((cp) => ({
          id: cp.id,
          bookingCode: cp.bookingCode,
          cruiseId: cp.cruise.id,
          cruiseName: cp.cruise.name,
          cruiseShipId: cp.cruiseShip?.id,
          cruiseShipName: cp.cruiseShip?.name,
          isGroupedPrice: cp.isGroupedPrice,
          startedAt: cp.startedAt,
          endedAt: cp.endedAt,
          reminder: cp.reminder,
          route: cp.route,
          cruisePurchaseAddOns: (cp.cruisePurchaseAddOns || []).map(
            (addon) => ({
              id: addon.id,
              description: addon.description,
              price: addon.price,
              guestId: addon.guest.id,
              guestName: `${capitalize(addon.guest.title)} ${addon.guest.name}`,
            }),
          ),
          cruisePurchaseGuests: (cp.cruisePurchaseGuests || []).map(
            (guest) => ({
              id: guest.id,
              guestId: guest.guest.id,
              guestName: `${capitalize(guest.guest.title)} ${guest.guest.name}`,
            }),
          ),
          cruisePurchaseDetails: (cp.cruisePurchaseDetails || []).map(
            (detail) => {
              const guest = cp.cruisePurchaseGuests?.find(
                (cpg) => cpg.id === detail.cruisePurchaseGuestId,
              )?.guest;
              return {
                id: detail.id,
                guestId: guest?.id || '',
                guestName: !cp.isGroupedPrice
                  ? `${capitalize(guest?.title)} ${guest?.name}`
                  : '',
                basicFare: detail.basicFare,
                portCharge: detail.portCharge,
                tax: detail.tax,
                tipping: detail.tipping,
                description: detail.description,
                cruiseRoomId: detail.cruiseRoom.id,
                cruiseRoomName: `${detail.cruiseRoom.type} (${detail.cruiseRoom.size})`,
                cruiseRoomNumber: detail.cruiseRoomNumber,
                deck: detail.deck,
              };
            },
          ),
        })),
      };
    }
    if (transactionType === 'product') {
      const productPurchase = data as ProductPurchaseModel;

      return {
        ...defaultValues,
        productPurchases: (productPurchase.productPurchases || []).map(
          (productPurchase) => ({
            productVariantName: productPurchase.completeName,
            description: productPurchase.description,
            price: productPurchase.price,
            qty: productPurchase.qty,
          }),
        ),
        productPurchaseOtherFees: (
          productPurchase.productPurchaseOtherFees || []
        ).map((fee) => ({
          name: fee.name,
          description: fee.description,
          price: fee.price,
        })),
      };
    }
    if (transactionType === 'flight') {
      const flightPurchase = data as FlightPurchaseModel;
      return {
        ...defaultValues,
        flightPurchases: (flightPurchase.flightPurchases || []).map((fp) => {
          const flightPurchaseGuests = distinct(
            fp.flightPurchaseGuests,
            (fpg) => fpg.guest.id,
          ).map((fpg) => {
            const guest = fpg.guest;
            return {
              id: fpg.id,
              guestId: guest.id,
              guestName: guestNameTransformer(guest),
              ticket: fpg.ticket,
            };
          });

          const flightPurchaseAirlines = distinct(
            fp.flightPurchaseGuests,
            (fpg) => generateUniqueAirlineItem(fpg),
          ).map((fpg) => {
            return {
              airlineId: fpg.airline.id,
              airlineName: fpg.airline.name,
              date: fpg.date,
              detail: fpg.detail,
              price: fpg.price,
              route: fpg.route,
              time: fpg.time,
              preferenceId: fpg?.preference?.id,
              preferenceName: fpg?.preference?.name,
            };
          });

          return {
            id: fp.id,
            bookingCode: fp.bookingCode,
            flightPurchaseAddOns: (fp.flightPurchaseAddOns || []).map(
              (fpao) => ({
                id: fpao.id,
                guestId: fpao.guest.id,
                guestName: `${capitalize(fpao.guest.title)} ${fpao.guest.name}`,
                route: fpao.route,
                description: fpao.description || '',
                price: fpao.price,
              }),
            ),
            flightPurchaseAirlines,
            flightPurchaseGuests,
          };
        }),
      };
    }
    if (transactionType === 'hotel') {
      const hotelPurchase = data as HotelPurchaseModel;
      return {
        ...defaultValues,

        hotelPurchases: (hotelPurchase.hotelPurchases || []).map((hp) => ({
          id: hp.id,
          hotelId: hp.hotel.id,
          hotelName: hp.hotel.name,
          hotelPhone: hp.hotel.phone,
          hotelEmail: hp.hotel.email,
          hotelAddress: hp.hotel.address,
          bookingCode: hp.bookingCode,
          hotelPurchaseAddOns: (hp.hotelPurchaseAddOns || []).map((hpao) => ({
            id: hpao.id,
            guestId: hpao.guest.id,
            guestName: `${capitalize(hpao.guest.title)} ${hpao.guest.name}`,
            description: hpao.description || '',
            price: hpao.price,
          })),
          hotelPurchaseHotels: (hp.hotelPurchaseHotels || []).map((hph) => ({
            id: hph.id,
            checkIn: hph.checkIn,
            checkOut: hph.checkOut,
            hotelRoomId: hph.hotelRoom.id,
            hotelRoomName: `${hph.hotelRoom.type} (${hph.hotelRoom.size})`,
            price: hph.price,
            room: hph.room,
            night: hph.night,
          })),
          hotelPurchaseGuests: (hp.hotelPurchaseGuests || []).map((hpg) => ({
            id: hpg.id,
            guestId: hpg.guest.id,
            guestName: `${capitalize(hpg.guest.title)} ${hpg.guest.name}`,
          })),
          cityTax: hp.cityTax,
          isCityTax: hp.isCityTax,
          isByPax: hp.isByPax,
          // remark: hp.remark,
        })),
      };
    }
    if (transactionType === 'document') {
      const documentPurchase = data as DocumentPurchaseModel;
      return {
        ...defaultValues,
        documentPurchases: (documentPurchase.documentPurchases || []).map(
          (document) => ({
            documentType: capitalize(document.documentType),
            documentPurchaseAddOns: (document.documentPurchaseAddOns || []).map(
              (addons) => ({
                guestId: addons.guest.id,
                guestName: `${capitalize(addons.guest.title)} ${
                  addons.guest.name
                }`,
                price: addons.price ? addons.price : 0,
                description: addons.description,
              }),
            ),
            documentPurchaseGuests: (
              document.documentPurchaseInsuranceGuests || []
            ).length
              ? (document.documentPurchaseInsuranceGuests || []).map(
                  (insurance) => ({
                    guestId: insurance.guest.id,
                    guestName: `${capitalize(insurance.guest.title)} ${
                      insurance.guest.name
                    }`,
                    type: insurance.details[0].type,
                    description: insurance.details[0].description,
                    price: insurance.details[0].price,
                    startedAt: insurance.details[0].startedAt,
                    endedAt: insurance.details[0].endedAt,
                    package: insurance.details[0].package,
                    company: insurance.details[0].company,
                    coverArea: insurance.details[0].coverArea,
                    days: insurance.details[0].days,
                  }),
                )
              : (document.documentPurchaseVisaGuests || []).length
              ? (document.documentPurchaseVisaGuests || []).map((visa) => ({
                  guestId: visa.guest.id,
                  guestName: `${capitalize(visa.guest.title)} ${
                    visa.guest.name
                  }`,
                  documentPurchaseGuestVisaTypes: (
                    visa.documentPurchaseVisaGuestDetails || []
                  ).map((visa) => ({
                    id: visa.visaDocument?.id || '',
                    label: visa.visaDocument?.name || '',
                    visaDocumentId: visa.visaDocument?.id || '',
                  })),
                  details: (visa.documentPurchaseVisaGuestDetails || []).map(
                    (detail) => {
                      return {
                        biometric: detail.biometric,
                        price: detail.price,
                        appointmentAt: detail.appointmentAt,
                        description: detail.description,
                        label: detail.visaDocument?.name,
                        visaDocumentId: detail.visaDocument?.id,
                      };
                    },
                  ),
                }))
              : (document.documentPurchasePassportGuests || []).length
              ? (document.documentPurchasePassportGuests || []).map(
                  (passport) => ({
                    guestId: passport.guest.id,
                    guestName: `${capitalize(passport.guest.title)} ${
                      passport.guest.name
                    }`,
                    type: passport.details.type,
                    description: passport.details.description,
                    price: passport.details.price,
                  }),
                )
              : [],
          }),
        ),
      };
    }
  }

  return defaultValues;
}
